
import { fieldLibrary, sections as defaultSections } from '../data';
import {
  Field,
  FormConfig,
  FormData,
  RuleGroup,
  SectionConfig,
  VisibilityRule,
} from '../types';

const STORAGE_KEY = 'loan-form-admin-config-v1';

/**
 * Flat lookup of every field that exists on the loan object — the fields placed
 * on the default layout plus the wider library admins can pull from.
 */
export const fieldCatalog: Record<string, Field> = (() => {
  const catalog: Record<string, Field> = {};
  defaultSections.forEach((section) => {
    section.fields.forEach((f) => {
      catalog[f.id] = f;
    });
  });
  fieldLibrary.forEach((f) => {
    catalog[f.id] = f;
  });
  return catalog;
})();

export const allFieldIds = Object.keys(fieldCatalog);

/** Field ids that appear on the default layout (everything else is library). */
const DEFAULT_LAYOUT_FIELD_IDS = defaultSections.flatMap((s) => s.fields.map((f) => f.id));

export function buildDefaultConfig(): FormConfig {
  return {
    sections: defaultSections.map<SectionConfig>((s) => ({
      id: s.id,
      title: s.title,
      icon: s.icon,
      hidden: false,
      columns: 2,
      defaultCollapsed: false,
      fieldIds: s.fields.map((f) => f.id),
    })),
    hiddenFieldIds: [],
    requiredFieldIds: [
      // Loan Details
      'loanName',
      'baseLoanAmount',
      'rcmLoanNumber',
      'stage',
      'workflowStatus',
      'totalLoanAmount',
      'interestRate',
      'ltv',
      'loanPurpose',
      'term',
      'fico',
      'typeOfLoan',
      'estimatedAppraisalValue',
      'mortgageType',
      'occupancy',
      'propertyType',
      'investor',
      'applicationDate',
      'rateLockAtReg',
      'lockExpirationDate',
      'earliestCloseDate',
      // Subject Property
      'propertyAddress',
      'propertyCity',
      'propertyState',
      'propertyZip',
      'propertyCounty',
      // Contact Information
      'accountName',
      'isCoBorrower',
      'isNbsTitleHolder',
      // User Information
      'loanOfficer',
      'manager',
      'team',
      'p1Concierge',
      'p2',
    ],
    ruleGroups: [{ id: 'group-coborrower', name: 'Co-Borrower Details' }],
    rules: [
      {
        id: 'rule-coborrower',
        groupId: 'group-coborrower',
        enabled: true,
        targetFieldId: 'coBorrowerFirstName',
        action: 'hide',
        conditionFieldId: 'isCoBorrower',
        operator: 'equals',
        conditionValue: 'No',
      },
    ],
    density: 'comfortable',
    showHeader: true,
    headerMetricIds: ['totalLoanAmount', 'interestRate', 'expectedPrice', 'earliestCloseDate', 'ltv'],
    showSearch: true,
    showChecklist: true,
  };
}

/* ---------------- persistence ---------------- */

export function loadConfig(): FormConfig {
  if (typeof window === 'undefined') return buildDefaultConfig();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return buildDefaultConfig();
    const parsed = JSON.parse(raw) as Partial<FormConfig>;
    if (!parsed?.sections?.length) return buildDefaultConfig();
    // Merge so configs saved before newer options gain their defaults.
    const merged = { ...buildDefaultConfig(), ...parsed } as FormConfig;
    return reconcileRuleGroups(reconcileFields(merged));
  } catch {
    return buildDefaultConfig();
  }
}

/**
 * Ensure fields added to the record since a config was saved still appear,
 * dropped into their original section (or the first one) rather than vanishing.
 */
function reconcileFields(config: FormConfig): FormConfig {
  const placed = new Set(config.sections.flatMap((s) => s.fieldIds));
  const missing = DEFAULT_LAYOUT_FIELD_IDS.filter((id) => !placed.has(id));
  if (missing.length === 0) return config;

  const sections = config.sections.map((s) => ({ ...s, fieldIds: [...s.fieldIds] }));

  missing.forEach((id) => {
    const home = defaultSections.find((s) => s.fields.some((f) => f.id === id));
    const target = sections.find((s) => s.id === home?.id) ?? sections[0];
    if (!target) return;
    const defaultIndex = home?.fields.findIndex((f) => f.id === id) ?? -1;
    if (defaultIndex >= 0 && defaultIndex <= target.fieldIds.length) {
      target.fieldIds.splice(defaultIndex, 0, id);
    } else {
      target.fieldIds.push(id);
    }
  });

  return { ...config, sections };
}

/**
 * Give every rule a home: rules saved before groups existed are bucketed by
 * the field they watch, and any missing group definition is recreated.
 */
function reconcileRuleGroups(config: FormConfig): FormConfig {
  const groups: RuleGroup[] = [...(config.ruleGroups ?? [])];
  const byId = new Map(groups.map((g) => [g.id, g]));
  const byField = new Map<string, string>();

  const rules = config.rules.map((rule) => {
    if (rule.groupId && byId.has(rule.groupId)) return rule;

    const fieldId = rule.conditionFieldId || 'unassigned';
    let groupId = rule.groupId && byId.has(rule.groupId) ? rule.groupId : byField.get(fieldId);

    if (!groupId) {
      groupId = rule.groupId || `group-${fieldId}`;
      if (!byId.has(groupId)) {
        const group = {
          id: groupId,
          name: fieldCatalog[fieldId]?.label ?? 'Ungrouped Rules',
        };
        groups.push(group);
        byId.set(groupId, group);
      }
      byField.set(fieldId, groupId);
    }

    return { ...rule, groupId };
  });

  return { ...config, ruleGroups: groups, rules };
}

export function saveConfig(config: FormConfig): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch {
    /* storage unavailable — config stays in memory */
  }
}

export function clearStoredConfig(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* no-op */
  }
}

/* ---------------- rule evaluation ---------------- */

function normalize(value: string | boolean | undefined): string {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value ?? '').trim();
}

export function conditionMet(rule: VisibilityRule, values: FormData): boolean {
  const actual = normalize(values[rule.conditionFieldId]);
  const expected = rule.conditionValue.trim();

  switch (rule.operator) {
    case 'equals':
      return actual.toLowerCase() === expected.toLowerCase();
    case 'notEquals':
      return actual.toLowerCase() !== expected.toLowerCase();
    case 'isEmpty':
      return actual === '';
    case 'isNotEmpty':
      return actual !== '';
    default:
      return false;
  }
}

/**
 * Resolve which fields are hidden, combining unconditional admin hides
 * with any conditional rules that currently match.
 */
export function computeHiddenFields(config: FormConfig, values: FormData): Set<string> {
  const hidden = new Set(config.hiddenFieldIds);

  config.rules.forEach((rule) => {
    if (!rule.enabled || !rule.targetFieldId || !rule.conditionFieldId) return;
    if (!conditionMet(rule, values)) return;
    if (rule.action === 'hide') hidden.add(rule.targetFieldId);
    else hidden.delete(rule.targetFieldId);
  });

  return hidden;
}

export function describeRule(rule: VisibilityRule): string {
  const target = fieldCatalog[rule.targetFieldId]?.label ?? 'Field';
  const condition = fieldCatalog[rule.conditionFieldId]?.label ?? 'field';
  const verb = rule.action === 'hide' ? 'Hide' : 'Show';

  switch (rule.operator) {
    case 'isEmpty':
      return `${verb} “${target}” when “${condition}” is empty`;
    case 'isNotEmpty':
      return `${verb} “${target}” when “${condition}” has a value`;
    case 'notEquals':
      return `${verb} “${target}” when “${condition}” is not “${rule.conditionValue || '—'}”`;
    default:
      return `${verb} “${target}” when “${condition}” is “${rule.conditionValue || '—'}”`;
  }
}

/* ---------------- immutable helpers ---------------- */

export function moveItem<T>(list: T[], index: number, direction: -1 | 1): T[] {
  const next = [...list];
  const target = index + direction;
  if (target < 0 || target >= next.length) return next;
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

export function createRuleId(): string {
  return `rule-${Math.random().toString(36).slice(2, 9)}`;
}

export function createSectionId(): string {
  return `section-${Math.random().toString(36).slice(2, 9)}`;
}

export function createGroupId(): string {
  return `group-${Math.random().toString(36).slice(2, 9)}`;
}

